// src/pages/AlphabetToolsPage.tsx - OPTIMIZED
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTools } from '../context/ToolContext';
import ToolCard from '../components/Home/ToolCard';
import { Search, ArrowLeft, Hash, TrendingUp } from 'lucide-react';
import InFeedAd from '../components/Ads/InFeedAd';

const AlphabetToolsPage = () => {
  const { letter } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { toolCategories } = useTools();
  
  const [searchTerm, setSearchTerm] = useState('');

  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
  
  const allTools = useMemo(() => 
    toolCategories.flatMap(category => 
      category.tools.map(tool => ({
        ...tool,
        category: category.title,
        categorySlug: category.title.toLowerCase().replace(/ /g, '-'),
        startingLetter: tool.title.charAt(0).toUpperCase()
      }))
    ),
    [toolCategories]
  );

  const filteredTools = useMemo(() => {
    let tools = allTools;

    if (letter && letter !== 'all') {
      const targetLetter = letter.toUpperCase();
      tools = tools.filter(tool => tool.startingLetter === targetLetter);
    }

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      tools = tools.filter(tool => 
        tool.title.toLowerCase().includes(searchLower) ||
        tool.description.toLowerCase().includes(searchLower) ||
        tool.tags?.some((tag: string) => tag.toLowerCase().includes(searchLower))
      );
    }

    return tools.sort((a, b) => a.title.localeCompare(b.title));
  }, [letter, searchTerm, allTools]);

  // SEO data - ENHANCED
  const seoData = useMemo(() => {
    const currentLetter = letter?.toUpperCase() || 'ALL';
    const toolsCount = filteredTools.length;
    const totalTools = allTools.length;
    const freeCount = filteredTools.filter(t => t.isFree).length;

    if (letter && letter !== 'all') {
      return {
        title: `${toolsCount} AI Tools Starting with ${currentLetter} - Alphabetical Directory | TooliQ.ai`,
        description: `Discover ${toolsCount} AI tools beginning with letter ${currentLetter}. ${freeCount} free options available. Browse artificial intelligence software alphabetically - updated daily.`,
        keywords: `AI tools ${currentLetter}, ${currentLetter} AI software, AI applications ${currentLetter}, artificial intelligence tools, alphabetical AI directory`,
        canonical: `https://tooliq.ai/alphabet/${letter}`
      };
    }

    return {
      title: `Complete A-Z AI Tools Directory - ${totalTools}+ Tools Alphabetically Organized | TooliQ.ai`,
      description: `Browse ${totalTools}+ AI tools from A to Z. Complete alphabetical directory of artificial intelligence software, applications, and platforms. Find AI tools by name.`,
      keywords: 'AI tools A-Z, alphabetical AI directory, AI software list, AI tools by name, artificial intelligence directory, browse AI tools alphabetically',
      canonical: 'https://tooliq.ai/alphabet/all'
    };
  }, [letter, filteredTools.length, allTools.length]);

  useEffect(() => {
    setSearchTerm('');
  }, [letter]);

  const handleLetterClick = (l: string) => {
    navigate(`/alphabet/${l.toLowerCase()}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleAllClick = () => {
    navigate('/alphabet/all');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const clearSearch = () => setSearchTerm('');

  const currentLetter = letter?.toUpperCase() || 'ALL';
  const toolsCount = filteredTools.length;
  const totalTools = allTools.length;
  const freeCount = filteredTools.filter(t => t.isFree).length;

  const getLetterCount = (l: string) => {
    return allTools.filter(tool => tool.startingLetter === l).length;
  };

  const popularLetters = useMemo(() => 
    alphabet
      .filter(l => getLetterCount(l) > 5)
      .sort((a, b) => getLetterCount(b) - getLetterCount(a))
      .slice(0, 8),
    [alphabet, allTools]
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/50 dark:from-gray-900 dark:to-blue-900/10">
      <Helmet>
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <meta name="keywords" content={seoData.keywords} />
        <link rel="canonical" href={seoData.canonical} />
        
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:url" content={seoData.canonical} />
        <meta property="og:type" content="website" />
        
        <meta name="twitter:title" content={seoData.title} />
        <meta name="twitter:description" content={seoData.description} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": seoData.title,
            "description": seoData.description,
            "url": seoData.canonical,
            "numberOfItems": toolsCount,
            "itemListElement": filteredTools.slice(0, 20).map((tool, index) => ({
              "@type": "ListItem",
              "position": index + 1,
              "item": {
                "@type": "SoftwareApplication",
                "name": tool.title,
                "description": tool.description,
                "applicationCategory": "AI Tool"
              }
            }))
          })}
        </script>
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header - ENHANCED */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mr-4 shadow-lg">
              <Hash className="w-8 h-8 text-white" />
            </div>
            <div className="text-left">
              <h1 className="text-4xl font-bold text-gray-900 dark:text-white">
                AI Tools A-Z Directory
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-2 text-lg">
                Browse {totalTools}+ tools alphabetically • {freeCount}+ free options
              </p>
            </div>
          </div>
        </div>

        {/* SEO Content Section - Only on "all" view */}
        {(!letter || letter === 'all') && (
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/10 dark:to-purple-900/10 rounded-2xl p-6 mb-8 border border-gray-200/50 dark:border-gray-700/50">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
              Complete Alphabetical AI Tools Directory
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              Explore our comprehensive A-Z directory featuring <strong>{totalTools}+ AI tools</strong> organized 
              alphabetically for easy discovery. Find <strong>free AI software</strong>, <strong>premium applications</strong>, 
              and specialized <strong>artificial intelligence platforms</strong> across all categories. Each tool is 
              carefully curated and regularly updated to ensure accuracy.
            </p>
          </div>
        )}

        {/* Alphabet Navigation - IMPROVED */}
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-sm mb-8">
          <div className="flex flex-wrap justify-center gap-2">
            <button
              onClick={handleAllClick}
              className={`px-4 py-3 rounded-xl font-semibold transition-all duration-300 ${
                (!letter || letter === 'all')
                  ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg scale-105'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
              aria-label="View all AI tools"
            >
              All ({totalTools})
            </button>

            {alphabet.map(l => {
              const toolsForLetter = getLetterCount(l);
              const isActive = letter === l.toLowerCase();
              
              return (
                <button
                  key={l}
                  onClick={() => handleLetterClick(l)}
                  disabled={toolsForLetter === 0}
                  className={`px-4 py-3 rounded-xl font-semibold transition-all duration-300 relative group ${
                    isActive
                      ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg scale-105'
                      : toolsForLetter > 0
                      ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 hover:scale-105'
                      : 'bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                  }`}
                  title={toolsForLetter === 0 ? 'No tools available' : `${toolsForLetter} tools starting with ${l}`}
                  aria-label={`View tools starting with ${l}`}
                >
                  {l}
                  {toolsForLetter > 0 && (
                    <span className={`absolute -top-1 -right-1 text-xs px-1 rounded-full min-w-[20px] ${
                      isActive
                        ? 'bg-white text-purple-600'
                        : 'bg-blue-500 text-white'
                    }`}>
                      {toolsForLetter}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Search Bar */}
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-sm mb-8">
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={
                  letter && letter !== 'all' 
                    ? `Search tools starting with ${currentLetter}...` 
                    : 'Search all AI tools by name, description, or features...'
                }
                className="w-full pl-12 pr-12 py-4 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                aria-label="Search AI tools"
              />
              {searchTerm && (
                <button
                  onClick={clearSearch}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors text-xl"
                  aria-label="Clear search"
                >
                  &times;
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Results Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {letter && letter !== 'all' ? `${currentLetter} Tools` : 'All AI Tools'}
              {searchTerm && ` matching "${searchTerm}"`}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {toolsCount} {toolsCount === 1 ? 'tool' : 'tools'} found
              {letter && letter !== 'all' && ` starting with ${currentLetter}`}
            </p>
          </div>
          
          <div className="hidden md:flex items-center gap-4 text-sm">
            <span className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-3 py-1 rounded-full">
              {freeCount} Free
            </span>
            <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-3 py-1 rounded-full">
              {new Set(filteredTools.map(t => t.category)).size} Categories
            </span>
          </div>
        </div>

        {/* Tools Grid - SINGLE AD after 12th tool */}
        {toolsCount > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
            {filteredTools.map((tool, index) => (
              <React.Fragment key={`${tool.categorySlug}-${tool.title}-${index}`}>
                <ToolCard
                  {...tool}
                  icon={tool.icon ?? '/default-icon.png'}
                  category={tool.category}
                />
                
                {/* SINGLE STRATEGIC AD - Only after 12th tool */}
                {index === 11 && filteredTools.length > 12 && (
                  <div className="col-span-1 sm:col-span-2 lg:col-span-3 xl:col-span-4 my-4">
                    <InFeedAd />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl border-2 border-dashed border-gray-300 dark:border-gray-600">
            <div className="w-20 h-20 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-2xl font-bold text-gray-700 dark:text-gray-300 mb-3">
              No tools found
            </h3>
            <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto mb-6 text-lg">
              {searchTerm
                ? `No tools found for "${searchTerm}" in ${currentLetter}. Try a different search.`
                : letter && letter !== 'all'
                ? `No tools starting with ${currentLetter}. Browse all tools instead.`
                : 'No tools match your criteria.'
              }
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <button
                onClick={handleAllClick}
                className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all font-semibold shadow-md"
              >
                Browse All Tools
              </button>
              {searchTerm && (
                <button
                  onClick={clearSearch}
                  className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Clear Search
                </button>
              )}
            </div>
          </div>
        )}

        {/* Popular Letters - Only on "all" view */}
        {(!letter || letter === 'all') && toolsCount > 0 && (
          <div className="mt-12 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-8 border border-gray-200/50 dark:border-gray-700/50">
            <div className="flex items-center justify-center gap-2 mb-6">
              <TrendingUp className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                Most Popular Starting Letters
              </h3>
            </div>
            <div className="flex flex-wrap justify-center gap-3">
              {popularLetters.map(l => {
                const count = getLetterCount(l);
                return (
                  <button
                    key={l}
                    onClick={() => handleLetterClick(l)}
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 text-blue-700 dark:text-blue-300 rounded-lg hover:from-blue-200 hover:to-purple-200 dark:hover:from-blue-800/50 dark:hover:to-purple-800/50 transition-all duration-300 hover:scale-105 border border-blue-200 dark:border-blue-700/50"
                  >
                    <span className="font-semibold">{l}</span>
                    <span className="text-sm opacity-75">({count} tools)</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Back Button */}
        <div className="text-center mt-12">
          <button
            onClick={() => navigate('/categories')}
            className="inline-flex items-center gap-2 px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-semibold"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Categories
          </button>
        </div>
      </div>
    </div>
  );
};

export default AlphabetToolsPage;